
import React, { useState, useRef, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { QRType, QRConfig, LanguageCode, FrameStyle } from '../types';
import { TRANSLATIONS, LANGUAGES } from '../constants';
import { Download, Copy, Palette, Type, Smartphone, Wifi, Mail, Phone, User, Globe, MessageSquare, X, Image as ImageIcon, Upload, Layout as LayoutIcon, Square, MessageCircle, BadgeCheck, Zap, Layers, PenTool, Facebook, Twitter, Youtube, FileText, CheckCircle2, ChevronDown, ChevronUp, Barcode, ArrowDown, ShieldCheck, Infinity, Paintbrush, Printer } from 'lucide-react';
import { useParams, useNavigate } from 'react-router-dom';

const DEFAULT_CONFIG: QRConfig = {
  value: '',
  fgColor: '#000000',
  bgColor: '#ffffff',
  size: 1024,
  level: 'H',
  includeMargin: false,
  imageSettings: undefined,
  frameStyle: 'none',
  frameText: 'SCAN ME',
  frameColor: '#000000',
  frameTextColor: '#ffffff'
};

const LOGO_OPTIONS = [
  { name: 'None', src: '' },
  // Social & Media
  { name: 'Facebook', src: '/icons/facebook.svg' },
  { name: 'Instagram', src: '/icons/instagram.svg' },
  { name: 'X', src: '/icons/x.svg' },
  { name: 'LinkedIn', src: '/icons/linkedin.svg' },
  { name: 'YouTube', src: '/icons/youtube.svg' },
  { name: 'Snapchat', src: '/icons/snapchat.svg' },
  // Communication
  { name: 'WhatsApp', src: '/icons/whatsapp.svg' },
  { name: 'Telegram', src: '/icons/telegram.svg' },
  { name: 'Gmail', src: '/icons/gmail.svg' },
  // Utility & Tech
  { name: 'Bitcoin', src: '/icons/bitcoin.svg' },
];

const BarcodeRenderer = ({ value, format, fgColor, bgColor }: { value: string, format: string, fgColor: string, bgColor: string }) => {
  const ref = useRef<SVGSVGElement>(null);

  useEffect(() => {
    let canceled = false;

    const renderBarcode = async () => {
      const node = ref.current;
      if (!node) return;
      if (!value) {
        node.innerHTML = '';
        return;
      }

      try {
        const { default: JsBarcode } = await import('jsbarcode');
        if (canceled) return;
        JsBarcode(node, value, {
          format: format as any,
          lineColor: fgColor,
          background: bgColor,
          displayValue: true,
          margin: 10,
          width: 2,
          height: 80,
          fontSize: 16
        });
      } catch (e) {
        console.warn("Invalid barcode value", e);
        if (ref.current) {
          ref.current.innerHTML = '';
        }
      }
    };

    void renderBarcode();
    return () => {
      canceled = true;
    };
  }, [value, format, fgColor, bgColor]);

  return <svg ref={ref} width="100%" />;
};

type GeneratorVariant = 'default' | 'text-to' | 'link-to';

interface QRCodeGeneratorProps {
  variant?: GeneratorVariant;
}

export const QRCodeGenerator: React.FC<QRCodeGeneratorProps> = ({ variant = 'default' }) => {
  const { lang } = useParams<{ lang: LanguageCode }>();
  const navigate = useNavigate();
  const currentLang = LANGUAGES[lang as LanguageCode] ? (lang as LanguageCode) : 'en';
  const t = TRANSLATIONS[currentLang];
  const tGen = t.generator;
  const tScan = t.scanner;
  
  // MAIN TABS STATE
  const [activeMainTab, setActiveMainTab] = useState<'content' | 'design'>('content');

  // Content State
  const [activeType, setActiveType] = useState<QRType>(QRType.URL);
  const [inputData, setInputData] = useState<any>({ url: '' });
  const [activeBarcodeFormat, setActiveBarcodeFormat] = useState<string>('CODE128');
  
  // Design State
  const [activeDesignTab, setActiveDesignTab] = useState<'frame' | 'colors' | 'logo'>('frame');
  const [designConfig, setDesignConfig] = useState<QRConfig>(DEFAULT_CONFIG);
  
  // Logo States
  const [customLogo, setCustomLogo] = useState<string | null>(null); // Base64 of uploaded logo
  const [activeLogoUrl, setActiveLogoUrl] = useState<string | null>(null); // To track which preset is selected for UI highlighting

  // Download State
  const [resolution, setResolution] = useState<number>(1024);
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const svgRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const previewRef = useRef<HTMLDivElement>(null);
  const formRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (variant === 'text-to') {
      setActiveType(QRType.TEXT);
    } else {
      setActiveType(QRType.URL);
    }
  }, [variant]);

  const calculateQRValue = () => {
    let finalValue = '';
    switch (activeType) {
      case QRType.URL: 
      case QRType.FACEBOOK:
      case QRType.TWITTER:
      case QRType.YOUTUBE:
        finalValue = inputData.url || ''; 
        break;
      case QRType.TEXT: finalValue = inputData.text || ''; break;
      case QRType.BARCODE: finalValue = inputData.text || '123456789'; break;
      case QRType.EMAIL: 
        finalValue = `mailto:${inputData.email || ''}?subject=${encodeURIComponent(inputData.subject || '')}&body=${encodeURIComponent(inputData.body || '')}`; 
        break;
      case QRType.PHONE: finalValue = `tel:${inputData.phone || ''}`; break;
      case QRType.SMS: finalValue = `smsto:${inputData.phone || ''}:${inputData.message || ''}`; break;
      case QRType.WIFI: finalValue = `WIFI:T:${inputData.encryption || 'WPA'};S:${inputData.ssid || ''};P:${inputData.password || ''};;`; break;
      case QRType.VCARD:
        const v: string[] = [];
        v.push('BEGIN:VCARD');
        v.push('VERSION:3.0');
        
        // Name is required in standard vCard, but we try to be robust
        v.push(`N:${inputData.vLast || ''};${inputData.vFirst || ''};;;`);
        v.push(`FN:${(inputData.vFirst || '')} ${(inputData.vLast || '')}`.trim());

        if (inputData.vMobile) v.push(`TEL;TYPE=CELL:${inputData.vMobile}`);
        if (inputData.vPhone) v.push(`TEL;TYPE=WORK:${inputData.vPhone}`);
        if (inputData.vFax) v.push(`TEL;TYPE=FAX:${inputData.vFax}`);
        if (inputData.vEmail) v.push(`EMAIL:${inputData.vEmail}`);
        if (inputData.vCompany) v.push(`ORG:${inputData.vCompany}`);
        if (inputData.vJob) v.push(`TITLE:${inputData.vJob}`);
        
        const street = inputData.vStreet || '';
        const city = inputData.vCity || '';
        const state = inputData.vState || '';
        const zip = inputData.vZip || '';
        const country = inputData.vCountry || '';
        
        // Only add address if at least one field is filled
        if (street || city || state || zip || country) {
             v.push(`ADR:;;${street};${city};${state};${zip};${country}`);
        }
        
        if (inputData.vWebsite) v.push(`URL:${inputData.vWebsite}`);
        
        v.push('END:VCARD');
        finalValue = v.join('\n');
        break;
    }
    return finalValue;
  };

  // derivedConfig combines the visual design settings with the calculated QR value
  // Uses a default URL if the calculated value is empty to ensure a valid QR is always shown
  const activeConfig: QRConfig = {
    ...designConfig,
    value: calculateQRValue() || (activeType === QRType.BARCODE ? '123456789' : 'https://yesqrcode.com')
  };

  const getCanvasFromSVG = (size: number): Promise<HTMLCanvasElement> => {
    return new Promise((resolve) => {
      const svgElement = svgRef.current?.querySelector('svg');
      if (!svgElement) return;

      const svgData = new XMLSerializer().serializeToString(svgElement);
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      // For barcodes, use natural size or scale
      // For QRs, use the passed size
      const viewBox = svgElement.getAttribute('viewBox')?.split(' ').map(Number);
      
      canvas.width = size;
      
      if (activeType === QRType.BARCODE) {
         // Keep aspect ratio for barcode
         const vbWidth = viewBox ? viewBox[2] : 200;
         const vbHeight = viewBox ? viewBox[3] : 100;
         const aspect = vbHeight / vbWidth;
         canvas.height = size * aspect;
      } else {
         canvas.height = size;
      }

      img.onload = () => {
        if (ctx) {
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          // Fill background if needed (especially for transparent SVGs)
          if (designConfig.bgColor) {
             ctx.fillStyle = designConfig.bgColor;
             ctx.fillRect(0, 0, canvas.width, canvas.height);
          }
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          resolve(canvas);
        }
      };
      // Encoding for special characters
      img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
    });
  };

  const handleDownloadPNG = async () => {
    const canvas = await getCanvasFromSVG(resolution);
    const link = document.createElement('a');
    link.download = `yesqrcode-${Date.now()}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  };

  const handleDownloadSVG = () => {
    const svgElement = svgRef.current?.querySelector('svg');
    if (!svgElement) return;
    const svgData = new XMLSerializer().serializeToString(svgElement);
    const blob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `yesqrcode-${Date.now()}.svg`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleDownloadPDF = async () => {
    const canvas = await getCanvasFromSVG(resolution); // Use current resolution
    const { jsPDF } = await import('jspdf');
    const doc = new jsPDF();
    const imgData = canvas.toDataURL('image/png');
    
    const margin = 20;
    const pageWidth = 210; // A4 width mm
    const maxWidth = pageWidth - (margin * 2);
    
    // Calculate aspect ratio
    const imgProps = doc.getImageProperties(imgData);
    const pdfHeight = (imgProps.height * maxWidth) / imgProps.width;

    doc.addImage(imgData, 'PNG', margin, margin, maxWidth, pdfHeight);
    doc.text("Scan me!", 105, margin + pdfHeight + 10, { align: "center" });
    doc.save(`yesqrcode-${Date.now()}.pdf`);
  };

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result as string;
        setCustomLogo(result);
        setActiveLogoUrl(null); // Deselect presets
        setDesignConfig(prev => ({ ...prev, imageSettings: { src: result, height: 40, width: 40, excavate: true } }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handlePresetLogoSelect = async (logo: { name: string, src: string }) => {
    setActiveLogoUrl(logo.src);
    // Note: We do not nullify customLogo here so users can switch back to their uploaded logo if they want (optional UI choice),
    // but typically selecting a preset overrides the current selection.

    if (!logo.src) {
      setDesignConfig(prev => ({ ...prev, imageSettings: undefined }));
      return;
    }

    try {
      // Fetch and convert to base64 to ensure offline compatibility and valid SVG exports
      const response = await fetch(logo.src);
      const blob = await response.blob();
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result as string;
        setDesignConfig(prev => ({
          ...prev,
          imageSettings: { src: base64, height: 40, width: 40, excavate: true }
        }));
      };
      reader.readAsDataURL(blob);
    } catch (error) {
      console.warn("Failed to convert logo to base64, falling back to URL.", error);
      // Fallback to URL if fetch fails (e.g. CORS issue)
      setDesignConfig(prev => ({
        ...prev,
        imageSettings: { src: logo.src, height: 40, width: 40, excavate: true }
      }));
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setInputData((prev: any) => ({ ...prev, [field]: value }));
  };

  const handleTypeChange = (newType: QRType) => {
    setActiveType(newType);

    if (newType === QRType.FACEBOOK) {
      setInputData((prev: any) => ({ ...prev, url: 'https://facebook.com/' }));
    } else if (newType === QRType.TWITTER) {
      setInputData((prev: any) => ({ ...prev, url: 'https://x.com/' }));
    } else if (newType === QRType.YOUTUBE) {
      setInputData((prev: any) => ({ ...prev, url: 'https://youtube.com/' }));
    } else if (newType === QRType.URL) {
      // If switching to generic URL and the current URL is just a social prefix, clear it
      const currentUrl = inputData.url;
      if (['https://facebook.com/', 'https://x.com/', 'https://youtube.com/'].includes(currentUrl)) {
        setInputData((prev: any) => ({ ...prev, url: '' }));
      }
    }
  };

  const getTypeIcon = (type: QRType) => {
    switch (type) {
      case QRType.URL: return <Globe size={18} />;
      case QRType.TEXT: return <Type size={18} />;
      case QRType.WIFI: return <Wifi size={18} />;
      case QRType.PHONE: return <Phone size={18} />;
      case QRType.SMS: return <MessageSquare size={18} />;
      case QRType.EMAIL: return <Mail size={18} />;
      case QRType.VCARD: return <User size={18} />;
      case QRType.FACEBOOK: return <Facebook size={18} />;
      case QRType.TWITTER: return <Twitter size={18} />;
      case QRType.YOUTUBE: return <Youtube size={18} />;
      case QRType.BARCODE: return <Barcode size={18} />;
      default: return <Smartphone size={18} />;
    }
  };

  const handleUrlFocus = () => {
    if (inputData.url) return;
    let prefix = '';
    if (activeType === QRType.FACEBOOK) prefix = 'https://facebook.com/';
    if (activeType === QRType.TWITTER) prefix = 'https://x.com/';
    if (activeType === QRType.YOUTUBE) prefix = 'https://youtube.com/';
    
    if (prefix) {
      setInputData((prev: any) => ({ ...prev, url: prefix }));
    }
  };

  const scrollToPreview = () => {
    if (previewRef.current) {
        const yOffset = -100; // Account for sticky header
        const y = previewRef.current.getBoundingClientRect().top + window.pageYOffset + yOffset;
        window.scrollTo({top: y, behavior: 'smooth'});
    }
  };
  
  const scrollToForm = () => {
    if (formRef.current) {
      const yOffset = -80;
      const y = formRef.current.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  // --- Components ---
  const MiniFramePreview = ({ style, selected }: { style: string, selected: boolean }) => {
    // Darker icon color for visibility
    const baseClass = selected ? 'text-blue-600' : 'text-slate-900'; 
    
    if (style === 'none') {
       return <div className={`w-full h-full flex items-center justify-center border-2 rounded-lg ${selected ? 'border-blue-500 bg-blue-50' : 'border-slate-200'}`}><X size={24} className={baseClass} /></div>;
    }
    
    return (
       <svg viewBox="0 0 100 120" className={`w-full h-auto ${baseClass}`}>
          {style === 'simple' && <path d="M5 5 H95 V95 H5 Z M5 100 H95 V115 H5 Z" fill="none" stroke="currentColor" strokeWidth="4" />}
          {style === 'balloon' && <path d="M5 5 H95 V85 H5 Z M45 85 L50 95 L55 85 M20 95 H80 V115 H20 Z" fill="none" stroke="currentColor" strokeWidth="4" />}
          {style === 'box' && <path d="M5 5 H95 V115 H5 Z M10 10 H90 V90 H10 Z" fill="none" stroke="currentColor" strokeWidth="4" />}
          {style === 'badge' && <path d="M5 5 H95 V95 H5 Z M20 85 H80 V105 H20 Z" fill="none" stroke="currentColor" strokeWidth="4" />}
          
          {/* New Styles */}
          {style === 'polaroid' && <path d="M5 5 H95 V115 H5 Z M15 15 H85 V85 H15 Z" fill="none" stroke="currentColor" strokeWidth="4" />}
          {style === 'phone' && <path d="M20 5 H80 Q90 5 90 15 V105 Q90 115 80 115 H20 Q10 115 10 105 V15 Q10 5 20 5 Z M20 20 H80 V90 H20 Z M45 100 H55" fill="none" stroke="currentColor" strokeWidth="4" />}
          {style === 'header' && <path d="M5 5 H95 V115 H5 Z M5 5 H95 V25 H5 Z" fill="none" stroke="currentColor" strokeWidth="4" />}
          {style === 'corner' && <path d="M5 5 H30 M5 5 V30 M95 5 H70 M95 5 V30 M5 115 H30 M5 115 V90 M95 115 H70 M95 115 V90" fill="none" stroke="currentColor" strokeWidth="4" />}

          <rect x="30" y="30" width="40" height="40" fill="currentColor" opacity="0.1" />
       </svg>
    );
  };

  const FramedQRCode = () => {
    // If Barcode mode, skip frames logic completely
    if (activeType === QRType.BARCODE) {
        return (
            <div className="flex items-center justify-center w-full h-full bg-white p-2">
                <BarcodeRenderer 
                    value={activeConfig.value} 
                    format={activeBarcodeFormat} 
                    fgColor={designConfig.fgColor} 
                    bgColor={designConfig.bgColor} 
                />
            </div>
        );
    }

    const QR_SIZE = 280;
    const qrComponent = (
      <QRCodeSVG
        value={activeConfig.value}
        size={QR_SIZE}
        fgColor={activeConfig.fgColor}
        bgColor={activeConfig.bgColor}
        level={activeConfig.level}
        includeMargin={activeConfig.includeMargin}
        imageSettings={activeConfig.imageSettings}
      />
    );

    if (activeConfig.frameStyle === 'none') return qrComponent;
    const { frameText, frameColor, frameTextColor } = activeConfig;
    let svgContent;

    if (activeConfig.frameStyle === 'simple') {
      const totalW = QR_SIZE + 40;
      const totalH = QR_SIZE + 100;
      svgContent = (
        <svg width="100%" height="100%" viewBox={`0 0 ${totalW} ${totalH}`} xmlns="http://www.w3.org/2000/svg">
          <rect x="0" y="0" width={totalW} height={totalH} fill="#ffffff" rx="15" />
          <g transform={`translate(20, 20)`}>{qrComponent}</g>
          <path d={`M 0 ${totalH - 60} H ${totalW} V ${totalH - 15} Q ${totalW} ${totalH} ${totalW - 15} ${totalH} H 15 Q 0 ${totalH} 0 ${totalH - 15} Z`} fill={frameColor} />
          <text x={totalW / 2} y={totalH - 25} textAnchor="middle" fill={frameTextColor} fontFamily="sans-serif" fontWeight="bold" fontSize="24">{frameText}</text>
        </svg>
      );
    } else if (activeConfig.frameStyle === 'balloon') {
      const totalW = QR_SIZE + 40;
      const totalH = QR_SIZE + 80;
      svgContent = (
        <svg width="100%" height="100%" viewBox={`0 0 ${totalW} ${totalH}`} xmlns="http://www.w3.org/2000/svg">
          <g transform={`translate(20, 0)`}>{qrComponent}</g>
          <g transform={`translate(0, ${QR_SIZE + 10})`}>
             <path d={`M ${totalW/2 - 10} 10 L ${totalW/2} 0 L ${totalW/2 + 10} 10 Z`} fill={frameColor} />
             <rect x="20" y="10" width={totalW - 40} height={50} rx="25" fill={frameColor} />
             <text x={totalW / 2} y={42} textAnchor="middle" fill={frameTextColor} fontFamily="sans-serif" fontWeight="bold" fontSize="22">{frameText}</text>
          </g>
        </svg>
      );
    } else if (activeConfig.frameStyle === 'box') {
      const totalW = QR_SIZE + 60;
      const totalH = QR_SIZE + 100;
      svgContent = (
        <svg width="100%" height="100%" viewBox={`0 0 ${totalW} ${totalH}`} xmlns="http://www.w3.org/2000/svg">
           <path d={`M 0 15 Q 0 0 15 0 H ${totalW - 15} Q ${totalW} 0 ${totalW} 15 V ${totalH - 15} Q ${totalW} ${totalH} ${totalW - 15} ${totalH} H 15 Q 0 ${totalH} 0 ${totalH - 15} Z`} fill={frameColor} />
           <rect x="10" y="10" width={totalW - 20} height={totalH - 70} fill="#ffffff" rx="5" />
           <g transform={`translate(30, 30)`}>{qrComponent}</g>
           <text x={totalW / 2} y={totalH - 22} textAnchor="middle" fill={frameTextColor} fontFamily="sans-serif" fontWeight="bold" fontSize="24">{frameText}</text>
        </svg>
      );
    } else if (activeConfig.frameStyle === 'badge') {
       const totalW = QR_SIZE + 50;
       const totalH = QR_SIZE + 80;
       svgContent = (
        <svg width="100%" height="100%" viewBox={`0 0 ${totalW} ${totalH}`} xmlns="http://www.w3.org/2000/svg">
           <rect x="0" y="0" width={totalW} height={QR_SIZE + 50} rx="20" fill="#ffffff" stroke={frameColor} strokeWidth="8" />
           <g transform={`translate(25, 25)`}>{qrComponent}</g>
           <g transform={`translate(${totalW/2 - 80}, ${QR_SIZE + 30})`}>
              <rect width="160" height="40" rx="20" fill={frameColor} />
              <text x="80" y="26" textAnchor="middle" fill={frameTextColor} fontFamily="sans-serif" fontWeight="bold" fontSize="18">{frameText}</text>
           </g>
        </svg>
       );
    } else if (activeConfig.frameStyle === 'polaroid') {
        const totalW = QR_SIZE + 40;
        const totalH = QR_SIZE + 120;
        svgContent = (
          <svg width="100%" height="100%" viewBox={`0 0 ${totalW} ${totalH}`} xmlns="http://www.w3.org/2000/svg">
            <rect x="0" y="0" width={totalW} height={totalH} fill="#ffffff" stroke={frameColor} strokeWidth="4" />
            <g transform={`translate(20, 20)`}>{qrComponent}</g>
            <text x={totalW / 2} y={totalH - 40} textAnchor="middle" fill={frameColor} fontFamily="sans-serif" fontWeight="bold" fontSize="28">{frameText}</text>
          </svg>
        );
    } else if (activeConfig.frameStyle === 'phone') {
        const totalW = QR_SIZE + 60;
        const totalH = QR_SIZE + 140;
        svgContent = (
          <svg width="100%" height="100%" viewBox={`0 0 ${totalW} ${totalH}`} xmlns="http://www.w3.org/2000/svg">
            <rect x="0" y="0" width={totalW} height={totalH} rx="30" fill={frameColor} />
            <rect x="15" y="40" width={totalW - 30} height={totalW - 30} fill="#ffffff" />
            <g transform={`translate(30, 55)`}>{qrComponent}</g>
            {/* Notch */}
            <rect x={totalW/2 - 30} y="10" width="60" height="10" rx="5" fill="#ffffff" opacity="0.3" />
            {/* Chin Text */}
            <text x={totalW / 2} y={totalH - 40} textAnchor="middle" fill={frameTextColor} fontFamily="sans-serif" fontWeight="bold" fontSize="20">{frameText}</text>
          </svg>
        );
    } else if (activeConfig.frameStyle === 'header') {
        const totalW = QR_SIZE + 40;
        const totalH = QR_SIZE + 100;
        svgContent = (
            <svg width="100%" height="100%" viewBox={`0 0 ${totalW} ${totalH}`} xmlns="http://www.w3.org/2000/svg">
                <rect x="0" y="0" width={totalW} height={totalH} fill="#ffffff" stroke={frameColor} strokeWidth="2" />
                <rect x="0" y="0" width={totalW} height="60" fill={frameColor} />
                <text x={totalW / 2} y="40" textAnchor="middle" fill={frameTextColor} fontFamily="sans-serif" fontWeight="bold" fontSize="24">{frameText}</text>
                <g transform={`translate(20, 80)`}>{qrComponent}</g>
            </svg>
        );
    } else if (activeConfig.frameStyle === 'corner') {
        const totalW = QR_SIZE + 60;
        const totalH = QR_SIZE + 60;
        const thick = 8;
        const len = 60;
        svgContent = (
            <svg width="100%" height="100%" viewBox={`0 0 ${totalW} ${totalH}`} xmlns="http://www.w3.org/2000/svg">
                <rect x="0" y="0" width={totalW} height={totalH} fill="#ffffff" />
                {/* Corners */}
                <path d={`M0 0 H${len} V${thick} H${thick} V${len} H0 Z`} fill={frameColor} />
                <path d={`M${totalW} 0 H${totalW - len} V${thick} H${totalW - thick} V${len} H${totalW} Z`} fill={frameColor} />
                <path d={`M0 ${totalH} H${len} V${totalH - thick} H${thick} V${totalH - len} H0 Z`} fill={frameColor} />
                <path d={`M${totalW} ${totalH} H${totalW - len} V${totalH - thick} H${totalW - thick} V${totalH - len} H${totalW} Z`} fill={frameColor} />
                
                <g transform={`translate(30, 30)`}>{qrComponent}</g>
            </svg>
        );
    }

    return svgContent;
  };

  const renderContentInput = () => {
    const inputClass = "w-full p-4 border border-slate-200 rounded-xl bg-slate-50 focus:bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all placeholder:text-slate-400 font-medium text-base";
    const labelClass = "block text-sm font-bold text-slate-500 uppercase tracking-wider mb-2 ml-1";
    
    switch (activeType) {
	      case QRType.URL:
	        return (
	          <div>
	            <label className={labelClass}>{tGen.labelUrl}</label>
	            <input 
	              aria-label={tGen.labelUrl}
	              type="url" 
	              placeholder={tGen.placeholders.url}
	              className={inputClass} 
	              value={inputData.url || ''} 
	              onChange={(e) => handleInputChange('url', e.target.value)}
	            />
	          </div>
	        );
      case QRType.BARCODE:
        return (
            <div className="space-y-4">
	                <div>
	                    <label className={labelClass}>{tGen.types[QRType.BARCODE]}</label>
	                    <input 
	                        aria-label={tGen.types[QRType.BARCODE]}
	                        type="text" 
	                        placeholder={tGen.placeholders.barcode}
	                        className={inputClass} 
	                        value={inputData.text || ''} 
	                        onChange={(e) => handleInputChange('text', e.target.value)} 
	                    />
	                </div>
	                <div>
	                    <label className={labelClass}>Format</label>
	                    <select 
	                        aria-label="Barcode format"
	                        className={inputClass} 
	                        value={activeBarcodeFormat} 
	                        onChange={(e) => setActiveBarcodeFormat(e.target.value)}
	                    >
                        <option value="CODE128">CODE128 (Default)</option>
                        <option value="CODE39">CODE39</option>
                        <option value="EAN13">EAN-13</option>
                        <option value="EAN8">EAN-8</option>
                        <option value="UPC">UPC</option>
                        <option value="ITF14">ITF-14</option>
                        <option value="MSI">MSI</option>
                        <option value="pharmacode">Pharmacode</option>
                    </select>
                </div>
            </div>
        );
	      case QRType.FACEBOOK:
	      case QRType.TWITTER:
	      case QRType.YOUTUBE:
	        return (
	          <div>
	            <label className={labelClass}>{tGen.types[activeType]}</label>
	            <input 
	              aria-label={tGen.types[activeType]}
	              type="url" 
	              placeholder="https://"
	              className={inputClass} 
	              value={inputData.url || ''} 
	              onChange={(e) => handleInputChange('url', e.target.value)}
	              onFocus={handleUrlFocus}
	            />
	          </div>
	        );
	      case QRType.TEXT:
	        return (<div><label className={labelClass}>{tGen.labelText}</label><textarea aria-label={tGen.labelText} rows={4} placeholder={tGen.placeholders.text} className={inputClass} value={inputData.text || ''} onChange={(e) => handleInputChange('text', e.target.value)} /></div>);
      case QRType.EMAIL:
        return (
          <div className="space-y-4">
             <div><label className={labelClass}>{tGen.vCard.email}</label><input type="email" placeholder={tGen.placeholders.email} className={inputClass} onChange={(e) => handleInputChange('email', e.target.value)} /></div>
             <div><label className={labelClass}>{tGen.placeholders.subject}</label><input type="text" placeholder={tGen.placeholders.subject} className={inputClass} onChange={(e) => handleInputChange('subject', e.target.value)} /></div>
             <div><label className={labelClass}>{t.contactForm.message}</label><textarea rows={4} placeholder={tGen.placeholders.message} className={inputClass} onChange={(e) => handleInputChange('body', e.target.value)} /></div>
          </div>
        );
      case QRType.PHONE:
        return (<div><label className={labelClass}>{tGen.types.PHONE}</label><input type="tel" placeholder={tGen.placeholders.phone} className={inputClass} onChange={(e) => handleInputChange('phone', e.target.value)} /></div>);
      case QRType.SMS:
        return (
          <div className="space-y-4">
             <div><label className={labelClass}>{tGen.vCard.phone}</label><input type="tel" placeholder={tGen.placeholders.phone} className={inputClass} onChange={(e) => handleInputChange('phone', e.target.value)} /></div>
             <div><label className={labelClass}>{t.contactForm.message}</label><textarea rows={4} placeholder={tGen.placeholders.message} className={inputClass} onChange={(e) => handleInputChange('message', e.target.value)} /></div>
          </div>
        );
	      case QRType.WIFI:
	        return (<div className="space-y-4"><div><label className={labelClass}>{tGen.labelSsid}</label><input aria-label={tGen.labelSsid} type="text" placeholder={tGen.placeholders.ssid} className={inputClass} onChange={(e) => handleInputChange('ssid', e.target.value)} /></div><div className="grid grid-cols-2 gap-4"><div><label className={labelClass}>{tGen.labelPassword}</label><input aria-label={tGen.labelPassword} type="password" placeholder={tGen.placeholders.password} className={inputClass} onChange={(e) => handleInputChange('password', e.target.value)} /></div><div><label className={labelClass}>{tGen.labelEncryption}</label><select aria-label={tGen.labelEncryption} className={inputClass} onChange={(e) => handleInputChange('encryption', e.target.value)}><option value="WPA">WPA/WPA2</option><option value="WEP">WEP</option><option value="nopass">None</option></select></div></div></div>);
      case QRType.VCARD:
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-[100px_1fr] gap-4 items-center">
              <label className="text-sm font-bold text-slate-500 uppercase">{t.contactForm.name}:</label>
              <div className="grid grid-cols-2 gap-4">
                 <input type="text" placeholder={tGen.vCard.lastName} className={inputClass} onChange={(e) => handleInputChange('vLast', e.target.value)} />
                 <input type="text" placeholder={tGen.vCard.firstName} className={inputClass} onChange={(e) => handleInputChange('vFirst', e.target.value)} />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-[100px_1fr] gap-4 items-center">
              <label className="text-sm font-bold text-slate-500 uppercase">Contact:</label>
              <input type="tel" placeholder={tGen.vCard.mobile} className={inputClass} onChange={(e) => handleInputChange('vMobile', e.target.value)} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-[100px_1fr] gap-4 items-center">
               <div className="hidden sm:block"></div>
               <div className="grid grid-cols-2 gap-4">
                 <input type="tel" placeholder={tGen.vCard.phone} className={inputClass} onChange={(e) => handleInputChange('vPhone', e.target.value)} />
                 <input type="tel" placeholder={tGen.vCard.fax} className={inputClass} onChange={(e) => handleInputChange('vFax', e.target.value)} />
               </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-[100px_1fr] gap-4 items-center">
              <label className="text-sm font-bold text-slate-500 uppercase">{tGen.vCard.email}:</label>
              <input type="email" placeholder={tGen.placeholders.email} className={inputClass} onChange={(e) => handleInputChange('vEmail', e.target.value)} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-[100px_1fr] gap-4 items-center">
              <label className="text-sm font-bold text-slate-500 uppercase">{tGen.vCard.company}:</label>
              <div className="grid grid-cols-2 gap-4">
                 <input type="text" placeholder={tGen.vCard.company} className={inputClass} onChange={(e) => handleInputChange('vCompany', e.target.value)} />
                 <input type="text" placeholder={tGen.vCard.job} className={inputClass} onChange={(e) => handleInputChange('vJob', e.target.value)} />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-[100px_1fr] gap-4 items-center">
              <label className="text-sm font-bold text-slate-500 uppercase">{tGen.vCard.street}:</label>
              <input type="text" placeholder={tGen.vCard.street} className={inputClass} onChange={(e) => handleInputChange('vStreet', e.target.value)} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-[100px_1fr] gap-4 items-center">
              <label className="text-sm font-bold text-slate-500 uppercase">{tGen.vCard.city}:</label>
              <div className="grid grid-cols-2 gap-4">
                 <input type="text" placeholder={tGen.vCard.city} className={inputClass} onChange={(e) => handleInputChange('vCity', e.target.value)} />
                 <input type="text" placeholder={tGen.vCard.zip} className={inputClass} onChange={(e) => handleInputChange('vZip', e.target.value)} />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-[100px_1fr] gap-4 items-center">
              <label className="text-sm font-bold text-slate-500 uppercase">{tGen.vCard.state}:</label>
              <input type="text" placeholder={tGen.vCard.state} className={inputClass} onChange={(e) => handleInputChange('vState', e.target.value)} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-[100px_1fr] gap-4 items-center">
              <label className="text-sm font-bold text-slate-500 uppercase">{tGen.vCard.country}:</label>
              <input type="text" placeholder={tGen.vCard.country} className={inputClass} onChange={(e) => handleInputChange('vCountry', e.target.value)} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-[100px_1fr] gap-4 items-center">
              <label className="text-sm font-bold text-slate-500 uppercase">{tGen.vCard.website}:</label>
              <input type="url" placeholder="www.your-website.com" className={inputClass} onChange={(e) => handleInputChange('vWebsite', e.target.value)} />
            </div>
          </div>
        );
      default:
        return (<div><label className={labelClass}>{tGen.types[activeType]}</label><input type="text" placeholder={tGen.placeholder} className={inputClass} onChange={(e) => handleInputChange((activeType as string).toLowerCase(), e.target.value)} /></div>);
    }
  };

  return (
    <div className="py-8 bg-[#F8FAFC] min-h-screen pb-24 sm:pb-8">
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
        {/* Changed layout order: Config section first in DOM (Left/Top), Preview section second (Right/Bottom) */}
        {/* Added gap-12 for mobile to increase spacing between modules as requested */}
        <div ref={formRef} className="flex flex-col lg:grid lg:grid-cols-3 gap-12 lg:gap-8 mb-16">
          
          {/* 1. CONFIGURATION SECTION (Top on Mobile, Left on Desktop) */}
          <div className="lg:col-span-2 space-y-6">
             
             {/* MAIN TOP TABS */}
             <div className="bg-white p-1.5 rounded-2xl shadow-sm border border-slate-200 inline-flex w-full sm:w-auto">
                <button 
                  onClick={() => setActiveMainTab('content')}
                  className={`flex-1 sm:flex-none px-6 py-3 rounded-xl font-bold text-base transition-all flex items-center justify-center gap-2 ${activeMainTab === 'content' ? 'bg-blue-600 text-white shadow-md' : 'text-slate-500 hover:bg-slate-50'}`}
                >
                  <Layers size={18} /> 1. {tGen.sectionContent}
                </button>
                <button 
                  onClick={() => setActiveMainTab('design')}
                  className={`flex-1 sm:flex-none px-6 py-3 rounded-xl font-bold text-base transition-all flex items-center justify-center gap-2 ${activeMainTab === 'design' ? 'bg-blue-600 text-white shadow-md' : 'text-slate-500 hover:bg-slate-50'}`}
                >
                  <PenTool size={18} /> 2. {tGen.sectionDesign}
                </button>
             </div>

             {/* TAB 1: CONTENT */}
             {activeMainTab === 'content' && (
               <div className="bg-white rounded-3xl p-6 sm:p-8 shadow-sm border border-slate-100 animate-in fade-in slide-in-from-bottom-2 duration-300">
                  
                  {/* Mobile Type Selector (Dropdown) */}
                  <div className="sm:hidden mb-6 relative">
                     <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2 ml-1">Content Type</label>
                     <div className="relative">
                        <div className="w-full flex items-center justify-between px-4 py-3 bg-white border border-slate-200 rounded-xl text-slate-700 font-bold shadow-sm">
                            <span className="flex items-center gap-3">
                                <span className="text-blue-600">{getTypeIcon(activeType)}</span>
                                {tGen.types[activeType]}
                            </span>
                            <ChevronDown size={18} className="text-slate-400"/>
                        </div>
	                        <select
	                            aria-label="Content type"
	                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
	                            value={activeType}
	                            onChange={(e) => handleTypeChange(e.target.value as QRType)}
	                        >
                            {Object.values(QRType).map((type) => (
                                <option key={type} value={type}>{tGen.types[type]}</option>
                            ))}
                        </select>
                     </div>
                  </div>

                  {/* Desktop Type Selector (Wrap List) */}
                  <div className="hidden sm:block mb-8">
                    <div className="flex flex-wrap gap-3">
                      {Object.values(QRType).map((type) => (
                        <button
                          key={type}
                          onClick={() => handleTypeChange(type)}
                          className={`flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-bold transition-all border ${
                            activeType === type 
                            ? 'bg-blue-50 border-blue-200 text-blue-700' 
                            : 'bg-white border-slate-100 text-slate-500 hover:border-slate-300'
                          }`}
                        >
                          {getTypeIcon(type)}
                          {tGen.types[type]}
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  {/* Inputs */}
                  {renderContentInput()}

                  {/* Mobile Generate Button - Added inside Content Tab at bottom */}
                  <button
                    onClick={scrollToPreview}
                    className="w-full py-3.5 mt-8 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold text-lg shadow-lg shadow-blue-500/20 active:scale-[0.98] transition-all flex items-center justify-center gap-2 md:hidden"
                  >
                    <ArrowDown size={20} />
                    {tGen.generateButton}
                  </button>
               </div>
             )}

             {/* TAB 2: DESIGN */}
             {activeMainTab === 'design' && (
               <div className="bg-white rounded-3xl p-6 sm:p-8 shadow-sm border border-slate-100 animate-in fade-in slide-in-from-bottom-2 duration-300">
                  {activeType === QRType.BARCODE ? (
                    <div className="text-center py-8 text-slate-500">
                        <p className="mb-4">{tGen.barcodeWarning}</p>
                        <p className="text-sm">{tGen.barcodeColors}</p>
                        <hr className="my-6 border-slate-100" />
                        
                        {/* Only Colors for Barcode */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 text-left">
                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase mb-2">{tGen.labelFgColor}</label>
                                <div className="flex items-center gap-3 p-1 rounded-xl border border-slate-200 bg-white">
                                <input type="color" value={designConfig.fgColor} onChange={(e) => setDesignConfig({ ...designConfig, fgColor: e.target.value })} className="h-10 w-14 rounded-lg cursor-pointer border-none bg-transparent" />
                                <input type="text" value={designConfig.fgColor} onChange={(e) => setDesignConfig({ ...designConfig, fgColor: e.target.value })} className="flex-1 bg-transparent font-mono text-sm" />
                                </div>
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase mb-2">{tGen.labelBgColor}</label>
                                <div className="flex items-center gap-3 p-1 rounded-xl border border-slate-200 bg-white">
                                <input type="color" value={designConfig.bgColor} onChange={(e) => setDesignConfig({ ...designConfig, bgColor: e.target.value })} className="h-10 w-14 rounded-lg cursor-pointer border-none bg-transparent" />
                                <input type="text" value={designConfig.bgColor} onChange={(e) => setDesignConfig({ ...designConfig, bgColor: e.target.value })} className="flex-1 bg-transparent font-mono text-sm" />
                                </div>
                            </div>
                        </div>
                    </div>
                  ) : (
                    <>
                    {/* Sub-Tabs for Design */}
                    <div className="flex border-b border-slate-100 mb-6 overflow-x-auto no-scrollbar">
                        {['frame', 'colors', 'logo'].map((subTab) => (
                        <button
                            key={subTab}
                            onClick={() => setActiveDesignTab(subTab as any)}
                            className={`px-6 py-3 font-bold text-sm flex items-center gap-2 transition-all border-b-2 whitespace-nowrap ${
                            activeDesignTab === subTab
                            ? 'border-blue-600 text-blue-600'
                            : 'border-transparent text-slate-400 hover:text-slate-600'
                            }`}
                        >
                            {subTab === 'frame' && <LayoutIcon size={18} />}
                            {subTab === 'colors' && <Palette size={18} />}
                            {subTab === 'logo' && <ImageIcon size={18} />}
                            {subTab === 'frame' ? tGen.sectionFrame : subTab === 'colors' ? tGen.sectionColors : tGen.sectionLogo}
                        </button>
                        ))}
                    </div>

                    {/* FRAME CONFIG */}
                    {activeDesignTab === 'frame' && (
                        <div className="space-y-6">
                        <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
                            {[
                                {id: 'none', label: tGen.frameStyles.none}, 
                                {id: 'simple', label: tGen.frameStyles.simple}, 
                                {id: 'balloon', label: tGen.frameStyles.balloon}, 
                                {id: 'box', label: tGen.frameStyles.box}, 
                                {id: 'badge', label: tGen.frameStyles.badge},
                                {id: 'polaroid', label: tGen.frameStyles.polaroid},
                                {id: 'phone', label: tGen.frameStyles.phone},
                                {id: 'header', label: tGen.frameStyles.header},
                                {id: 'corner', label: tGen.frameStyles.corner}
                            ].map((style) => (
                            <button
                                key={style.id}
                                onClick={() => setDesignConfig({ ...designConfig, frameStyle: style.id as FrameStyle })}
                                className={`flex flex-col items-center p-2 rounded-xl border-2 transition-all h-24 justify-center bg-white ${designConfig.frameStyle === style.id ? 'border-blue-600 bg-blue-50/50' : 'border-slate-100 hover:border-blue-200'}`}
                            >
                                <div className="w-10 h-10 mb-2"><MiniFramePreview style={style.id} selected={designConfig.frameStyle === style.id} /></div>
                                <span className="text-[10px] uppercase font-bold text-slate-500">{style.label}</span>
                            </button>
                            ))}
                        </div>
                        {designConfig.frameStyle !== 'none' && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div><label className="block text-xs font-bold text-slate-500 uppercase mb-1">{tGen.labelFrameText}</label><input type="text" value={designConfig.frameText} onChange={(e) => setDesignConfig({ ...designConfig, frameText: e.target.value })} className="w-full p-3 border border-slate-200 rounded-xl" /></div>
                            <div><label className="block text-xs font-bold text-slate-500 uppercase mb-1">{tGen.labelFrameColor}</label><div className="flex gap-2"><input type="color" value={designConfig.frameColor} onChange={(e) => setDesignConfig({ ...designConfig, frameColor: e.target.value })} className="h-10 w-12 rounded-lg border border-slate-200" /><input type="text" value={designConfig.frameColor} onChange={(e) => setDesignConfig({ ...designConfig, frameColor: e.target.value })} className="flex-1 p-2 border border-slate-200 rounded-lg font-mono uppercase" /></div></div>
                            </div>
                        )}
                        </div>
                    )}

                    {/* COLORS CONFIG */}
                    {activeDesignTab === 'colors' && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-xs font-bold text-slate-500 uppercase mb-2">{tGen.labelFgColor}</label>
                            <div className="flex items-center gap-3 p-1 rounded-xl border border-slate-200 bg-white">
                            <input type="color" value={designConfig.fgColor} onChange={(e) => setDesignConfig({ ...designConfig, fgColor: e.target.value })} className="h-10 w-14 rounded-lg cursor-pointer border-none bg-transparent" />
                            <input type="text" value={designConfig.fgColor} onChange={(e) => setDesignConfig({ ...designConfig, fgColor: e.target.value })} className="flex-1 bg-transparent font-mono text-sm" />
                            </div>
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-slate-500 uppercase mb-2">{tGen.labelBgColor}</label>
                            <div className="flex items-center gap-3 p-1 rounded-xl border border-slate-200 bg-white">
                            <input type="color" value={designConfig.bgColor} onChange={(e) => setDesignConfig({ ...designConfig, bgColor: e.target.value })} className="h-10 w-14 rounded-lg cursor-pointer border-none bg-transparent" />
                            <input type="text" value={designConfig.bgColor} onChange={(e) => setDesignConfig({ ...designConfig, bgColor: e.target.value })} className="flex-1 bg-transparent font-mono text-sm" />
                            </div>
                        </div>
                        </div>
                    )}

                    {/* LOGO CONFIG */}
                    {activeDesignTab === 'logo' && (
                        <div className="flex flex-wrap gap-4">
                        <button onClick={() => fileInputRef.current?.click()} className="h-16 px-6 rounded-2xl border border-dashed border-blue-300 bg-blue-50 text-blue-600 font-bold text-xs flex flex-col items-center justify-center gap-1"><Upload size={18} /> {tGen.uploadLogo}</button>
                        <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={handleLogoUpload} />
                        
	                        {customLogo && (
	                            <button 
	                                onClick={() => {
	                                    setActiveLogoUrl(null);
	                                    setDesignConfig(prev => ({ ...prev, imageSettings: { src: customLogo, height: 40, width: 40, excavate: true } }));
	                                }}
	                                type="button"
	                                aria-label="Custom logo"
	                                className={`h-16 w-16 rounded-2xl border flex items-center justify-center bg-white overflow-hidden relative ${!activeLogoUrl && designConfig.imageSettings?.src === customLogo ? 'border-blue-500 shadow-md ring-2 ring-blue-100' : 'border-slate-200'}`}
	                            >
	                                <img src={customLogo} className="w-full h-full object-cover" alt="Custom" />
	                            </button>
	                        )}

	                        {LOGO_OPTIONS.map((logo, idx) => (
	                            <button 
	                                key={idx} 
	                                onClick={() => handlePresetLogoSelect(logo)} 
	                                type="button"
	                                aria-label={logo.name}
	                                className={`h-16 w-16 rounded-2xl border flex items-center justify-center bg-white ${activeLogoUrl === logo.src ? 'border-blue-500 shadow-md ring-2 ring-blue-100' : 'border-slate-200'}`}
	                            >
	                            {logo.src ? <img src={logo.src} className="h-8 w-8 object-contain" alt={logo.name} /> : <X size={20} className="text-slate-400" />}
	                            </button>
	                        ))}
                        </div>
                    )}
                    </>
                  )}
               </div>
             )}

          </div>

          {/* 2. PREVIEW SECTION (Bottom on Mobile, Right on Desktop) */}
          <div className="lg:col-span-1" ref={previewRef}>
            <div className="lg:sticky lg:top-28 space-y-4">
              <div className="bg-white rounded-[2rem] p-6 sm:p-8 shadow-xl shadow-slate-200/50 border border-slate-100 flex flex-col items-center relative overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500"></div>
                
                {/* QR Preview Area */}
                <div ref={svgRef} className={`bg-white p-2 sm:p-4 rounded-xl shadow-inner border border-slate-100 mb-6 w-full ${activeType !== QRType.BARCODE ? 'max-w-[280px] aspect-square' : ''} flex items-center justify-center mx-auto`}>
                   <FramedQRCode />
                </div>

                {/* Resolution Slider & Download Buttons - Now visible on mobile */}
                <div className="w-full space-y-4 block">
                    <div className="w-full mb-6">
                        <div className="flex justify-between text-xs font-bold text-slate-500 mb-3 uppercase tracking-wider">
                        <span>{tGen.lowQuality}</span>
                        <span className="text-blue-600 bg-blue-50 px-2 py-0.5 rounded">{resolution}px</span>
                        <span>{tGen.highQuality}</span>
                        </div>
                        <div className="relative flex items-center">
                            <label htmlFor="qr-resolution" className="sr-only">
                              {tGen.lowQuality} – {tGen.highQuality} ({resolution}px)
                            </label>
                            <input 
                                id="qr-resolution"
                                type="range" 
                                min="300" 
                                max="3000" 
                                step="100"
                                value={resolution}
                                onChange={(e) => setResolution(Number(e.target.value))}
                                aria-valuetext={`${resolution}px`}
                                className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-blue-600 hover:accent-blue-700 transition-all"
                            />
                        </div>
                    </div>

                    <div className="w-full space-y-3">
                        <button 
                            onClick={handleDownloadPNG}
                            className="w-full flex items-center justify-center gap-2 py-4 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold transition-all shadow-lg shadow-blue-200 active:scale-[0.98]"
                        >
                            <Download size={20} /> {tGen.download}
                        </button>

                        <div className="grid grid-cols-2 gap-3">
                            <button 
                                onClick={handleDownloadSVG}
                                className="flex items-center justify-center gap-2 py-3 bg-white border border-slate-200 hover:border-slate-300 hover:bg-slate-50 text-slate-700 rounded-xl font-bold transition-colors text-sm"
                            >
                                <Zap size={16} /> .SVG
                            </button>
                            <button 
                                onClick={handleDownloadPDF}
                                className="flex items-center justify-center gap-2 py-3 bg-white border border-slate-200 hover:border-slate-300 hover:bg-slate-50 text-slate-700 rounded-xl font-bold transition-colors text-sm"
                            >
                                <FileText size={16} /> .PDF
                            </button>
                        </div>
                    </div>
                    <p className="text-center text-xs text-slate-500 font-medium pt-2">
                        {tGen.foreverFree}
                    </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* MODULE 1: HERO + KEY BENEFITS (moved below generator) */}
        <section className="mb-6 md:mb-8 rounded-3xl bg-white shadow-sm border border-slate-200 p-8 md:p-12">
          <div className="max-w-5xl mx-auto space-y-8">
            <div className="text-center space-y-3">
              <h1 className="text-3xl md:text-4xl font-extrabold text-slate-900 leading-tight">
                {tGen.highlights?.title || t.hero.title}
              </h1>
              <p className="text-lg text-slate-600 leading-relaxed">
                {tGen.highlights?.subtitle || t.hero.subtitle}
              </p>
            </div>

            {tGen.highlights && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {tGen.highlights.items.map((item, idx) => {
                  const badges = [
                    <ShieldCheck size={18} />,
                    <Infinity size={18} />,
                    <Paintbrush size={18} />,
                    <Smartphone size={18} />,
                  ];
                  const badge = badges[idx] || badges[badges.length - 1];
                  return (
                    <div key={idx} className="bg-slate-50 rounded-2xl border border-slate-200 p-5 flex flex-col gap-3 h-full">
                      <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-blue-50 text-blue-600 text-sm font-bold">
                        {badge}
                      </div>
                      <div className="space-y-1">
                        <p className="font-bold text-slate-900">{item.title}</p>
                        <p className="text-slate-600 text-sm leading-relaxed">{item.desc}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </section>

        {/* CONTENT SECTIONS */}
        <div className="space-y-12 pt-8 md:pt-10">
          <div className="max-w-6xl mx-auto space-y-16">
            {/* WHY CHOOSE (combined highlights + about) */}
            {tGen.featureSuite && (
              <section className="rounded-3xl p-8 md:p-12 bg-white border border-slate-200 shadow-sm">
                <div className="space-y-10">
                  <div className="text-center space-y-3">
                    <h2 className="text-3xl font-bold text-slate-900">{tGen.featureSuite.title}</h2>
                    <p className="text-slate-600 text-lg leading-relaxed">{tGen.featureSuite.subtitle}</p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {tGen.featureSuite.categories.map((cat, idx) => (
                      <div key={idx} className="rounded-2xl border border-slate-200 p-6 bg-slate-50/60 hover:bg-white transition-colors h-full shadow-sm">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-blue-50 text-blue-600">
                            {idx === 0 ? <Globe size={18} /> : idx === 1 ? <Paintbrush size={18} /> : idx === 2 ? <Printer size={18} /> : <Smartphone size={18} />}
                          </div>
                          <h3 className="text-xl font-bold text-slate-900">{cat.title}</h3>
                        </div>
                        <p className="text-slate-600 leading-relaxed mb-4">{cat.body}</p>
                        <ul className="space-y-2 text-slate-700 text-sm">
                          {cat.items.map((item, i) => (
                            <li key={i} className="flex items-start gap-2">
                              <CheckCircle2 size={16} className="text-green-500 mt-0.5 flex-shrink-0" />
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>
              </section>
            )}

            {/* HOW TO USE */}
            <section>
              <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">{tGen.gettingStarted.title}</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
                {tGen.gettingStarted.steps.map((step, idx) => (
                  <div key={idx} className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm flex flex-col gap-4">
                    <div className="w-10 h-10 rounded-xl bg-blue-600 text-white font-bold flex items-center justify-center text-lg shadow-lg shadow-blue-200">
                      {idx + 1}
                    </div>
                    <div>
                      <h3 className="font-bold text-slate-900 mb-2">{step.title}</h3>
                      <p className="text-slate-500 text-base leading-relaxed">{step.text}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* FAQ */}
            <section className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">{tGen.faq.title}</h2>
              <div className="space-y-4">
                {tGen.faq.items.map((item, idx) => (
                  <div key={idx} className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
                    <button
                      onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                      className="w-full flex items-center justify-between p-6 text-left hover:bg-slate-50 transition-colors"
                    >
                      <span className="font-bold text-slate-900 text-lg">{item.q}</span>
                      {openFaq === idx ? <ChevronUp size={20} className="text-slate-400" /> : <ChevronDown size={20} className="text-slate-400" />}
                    </button>
                    {openFaq === idx && (
                      <div className="px-6 pb-6 pt-0 text-slate-600 text-base leading-relaxed animate-in slide-in-from-top-2">
                        {item.a}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>
          </div>
        </div>

        {/* Removed Sticky Mobile Footer as per request */}
      </div>
    </div>
  );
};
